'use client';

import { type Product } from '@/lib/productService';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTranslations } from 'next-intl';
import { ProductMedia } from '@/components/product-media';
import Link from 'next/link';
import { useState, memo } from 'react';
import { ShoppingCart, Check } from 'lucide-react';
import { AddToCartDialog } from '@/components/add-to-cart-dialog';
import { ProductVariantDialog } from '@/components/product-variant-dialog';
import { formatCurrency } from '@/lib/utils';
import { useProductCardActions, getCatalogEmoji } from '@/hooks/use-product-card-actions';

export type ProductCardProps = {
  product: Product;
  storeId: string;
  publicStoreId?: string;
  locale: string;
  isStoreClosed?: boolean;
};

function ProductCardComponent({
  product,
  storeId,
  publicStoreId,
  locale,
  isStoreClosed = false,
}: ProductCardProps) {
  const t = useTranslations('product');
  const [imageError, setImageError] = useState(false);

  const {
    isAdding,
    justAdded,
    showDialog,
    setShowDialog,
    showVariantDialog,
    setShowVariantDialog,
    isOutOfStock,
    isInactive,
    isPresale,
    isUnavailable,
    handleAddToCart,
  } = useProductCardActions(product, storeId, isStoreClosed);

  const presaleDateLabel = product.launchAt
    ? new Date(product.launchAt).toLocaleDateString(locale, { day: '2-digit', month: '2-digit' })
    : '';

  const storePathSegment = publicStoreId ?? storeId;
  const productUrl = `/${locale}/${storePathSegment}/product/${product.id}`;
  const formattedPrice = formatCurrency(product.price, locale);
  const hasPromotion =
    !!product.promotionPrice &&
    product.promotionPrice > 0 &&
    product.price > 0 &&
    product.promotionPrice < product.price;
  const discountPercent = hasPromotion
    ? Math.round(((product.price - product.promotionPrice!) / product.price) * 100)
    : null;

  return (
    <>
      <AddToCartDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        productName={product.name}
        storeId={storeId}
        publicStoreId={storePathSegment}
        locale={locale}
        translations={{
          title: t('addedToCart'),
          goToCart: t('goToCart'),
          continueShopping: t('continueShopping'),
        }}
      />
      <ProductVariantDialog
        open={showVariantDialog}
        onOpenChange={setShowVariantDialog}
        product={product}
        storeId={storeId}
        publicStoreId={storePathSegment}
        locale={locale}
      />
      <Link href={productUrl} prefetch={false} className="block h-full">
        <div className="flex h-full flex-col overflow-hidden rounded-4xl bg-secondary text-secondary-foreground">
          {/* Image inset inside card */}
          <div className="p-3">
            <div className="relative aspect-square w-full overflow-hidden rounded-4xl bg-muted">
              {/* Brand Badge - Top Left */}
              {product.brand && (
                <div className="absolute top-1 left-2 z-10">
                  <Badge variant="default">
                    {product.brand}
                  </Badge>
                </div>
              )}

              {/* Pre-sale Badge - Top Right (Vitrine Viva) */}
              {isPresale && (
                <div className="absolute top-2 right-2 z-20">
                  <Badge variant="default" className="bg-primary text-primary-foreground hover:bg-primary">
                    {presaleDateLabel ? t('presaleAvailableOn', { date: presaleDateLabel }) : t('presaleBadge')}
                  </Badge>
                </div>
              )}

              {/* Product Image */}
              {product.imageUrl && !imageError ? (
                <ProductMedia
                  src={product.imageUrl}
                  alt={product.name}
                  fill
                  className="object-cover"
                  onError={() => setImageError(true)}
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  autoPlay
                  loop
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-muted">
                  <span className="text-6xl">{getCatalogEmoji(product.catalogType)}</span>
                </div>
              )}

              {/* Availability Badge (pre-sale shows its own badge instead) */}
              {isUnavailable && !isPresale && (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/50 px-3 text-center">
                  <Badge variant="default" className="text-center">
                    {isOutOfStock && !isInactive ? t('outOfStock') : t('unavailable')}
                  </Badge>
                </div>
              )}

              {/* Floating Add to Cart */}
              {!isUnavailable && (
                <Button
                  type="button"
                  size="icon"
                  className="absolute bottom-2 right-2 z-10 size-10 rounded-full shadow-md"
                  disabled={isAdding}
                  aria-label={
                    justAdded
                      ? t('addedToCart')
                      : isStoreClosed
                        ? t('storeClosed')
                        : isOutOfStock
                          ? t('outOfStock')
                          : isInactive
                            ? t('unavailable')
                            : t('addToCart')
                  }
                  onClick={handleAddToCart}
                >
                  {justAdded ? (
                    <Check className="size-5" />
                  ) : (
                    <ShoppingCart className="size-5" />
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* Product Info */}
          <div className="flex-1 px-3 pb-3">
            {/* Price */}
            {hasPromotion ? (
              <div className="mb-1.5">
                <p className="text-base font-semibold text-green-600">
                  {formatCurrency(product.promotionPrice!, locale)}
                </p>
                <div className="flex items-center gap-1.5">
                  <p className="text-xs text-muted-foreground line-through">
                    {formattedPrice}
                  </p>
                  {discountPercent !== null && discountPercent > 0 && (
                    <Badge className="h-4 border-transparent bg-green-600 px-1 py-0 text-[9px] leading-none text-white hover:bg-green-600">
                      -{discountPercent}%
                    </Badge>
                  )}
                </div>
              </div>
            ) : (
              <p className="mb-1.5 text-base font-semibold text-foreground">
                {formattedPrice}
              </p>
            )}

            {/* Product Name - 2 line limit with ellipsis */}
            <h3
              className="mb-1.5 line-clamp-2 min-h-[2.25rem] text-sm font-medium leading-snug"
              title={product.name}
            >
              {product.name}
            </h3>

            {/* Category */}
            {product.category && (
              <p className="mb-1 text-xs text-muted-foreground">
                {product.category}
              </p>
            )}

            {/* Volume and Unit */}
            {(product.volume || product.unit) && (
              <p className="mb-1 text-xs text-muted-foreground">
                {product.volume} {product.unit}
              </p>
            )}
          </div>
        </div>
      </Link>
    </>
  );
}

/** Avoid re-rendering the whole grid when the parent only toggles `loadingMore` (same product references stay stable). */
export const ProductCard = memo(
  ProductCardComponent,
  (a, b) =>
    a.product === b.product &&
    a.storeId === b.storeId &&
    a.publicStoreId === b.publicStoreId &&
    a.locale === b.locale &&
    a.isStoreClosed === b.isStoreClosed
);
